/**
 * Maintenance Dashboard Component
 * Shows key metrics at the top of maintenance views
 * 
 * Metrics:
 * - Overdue jobs (red)
 * - This week jobs (blue)
 * - Completed % this month (green)
 * - Pending notifications (yellow)
 */

import React from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Error as OverdueIcon,
  EventAvailable as ThisWeekIcon,
  CheckCircle as CompletedIcon,
  Notifications as NotifyIcon
} from '@material-ui/icons';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  card: {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  cardContent: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: 48,
  },
  overdueIcon: {
    color: '#f44336',
  },
  thisWeekIcon: {
    color: '#2196f3',
  },
  completedIcon: {
    color: '#4caf50',
  },
  notifyIcon: {
    color: '#ff9800',
  },
  number: {
    fontSize: 36,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  subtitle: {
    fontSize: 12,
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.5),
  },
}));

const MaintenanceDashboard = ({ jobs, onFilterClick, isLoading }) => {
  const classes = useStyles();

  // Calculate metrics
  const calculateMetrics = () => {
    if (!jobs || jobs.length === 0) {
      return {
        overdue: 0,
        thisWeek: 0,
        completedPercent: 0,
        pendingNotifications: 0,
      };
    }

    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 0 }); // Sunday
    const weekEnd = endOfWeek(now, { weekStartsOn: 0 });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    // Overdue jobs
    const overdue = jobs.filter(job => job.status === 'overdue').length;

    // This week jobs (scheduled between week start and end, not completed/cancelled)
    const thisWeek = jobs.filter(job => {
      const scheduled = new Date(job.scheduled_date);
      return scheduled >= weekStart && 
             scheduled <= weekEnd && 
             !['completed', 'cancelled', 'skipped'].includes(job.status);
    }).length;

    // Completed % this month
    const thisMonthJobs = jobs.filter(job => {
      const scheduled = new Date(job.scheduled_date);
      return scheduled >= monthStart && scheduled <= monthEnd;
    });
    const completedThisMonth = thisMonthJobs.filter(job => job.status === 'completed').length;
    const completedPercent = thisMonthJobs.length > 0 
      ? Math.round((completedThisMonth / thisMonthJobs.length) * 100) 
      : 0;

    // Pending notifications (jobs where notification should happen but hasn't)
    const pendingNotifications = jobs.filter(job => {
      // Only pending/in_progress jobs
      if (!['pending', 'in_progress'].includes(job.status)) return false;
      
      // Client notification pending (if enabled)
      const clientPending = job.notification_days_before > 0 && !job.client_notified_at;
      
      // Engineer reminder pending (if within 3 days and not sent)
      const scheduled = new Date(job.scheduled_date);
      const daysUntil = Math.ceil((scheduled - now) / (1000 * 60 * 60 * 24));
      const engineerPending = daysUntil <= 3 && daysUntil >= 0 && !job.engineer_reminded_at;
      
      return clientPending || engineerPending;
    }).length;

    return {
      overdue,
      thisWeek,
      completedPercent,
      pendingNotifications,
    };
  };

  const metrics = calculateMetrics();

  const cards = [
    {
      id: 'overdue',
      icon: OverdueIcon,
      iconClass: classes.overdueIcon,
      number: metrics.overdue,
      label: 'Overdue',
      subtitle: 'Past due date',
      filter: { status: 'overdue' },
    },
    {
      id: 'thisWeek',
      icon: ThisWeekIcon,
      iconClass: classes.thisWeekIcon,
      number: metrics.thisWeek,
      label: 'This Week',
      subtitle: format(startOfWeek(new Date(), { weekStartsOn: 0 }), 'MMM d') + ' - ' + 
                format(endOfWeek(new Date(), { weekStartsOn: 0 }), 'MMM d'),
      filter: { dateRange: 'week' },
    },
    {
      id: 'completed',
      icon: CompletedIcon,
      iconClass: classes.completedIcon,
      number: `${metrics.completedPercent}%`,
      label: 'Completed',
      subtitle: format(new Date(), 'MMMM yyyy'),
      filter: { status: 'completed', dateRange: 'month' },
    },
    {
      id: 'pendingNotify',
      icon: NotifyIcon,
      iconClass: classes.notifyIcon,
      number: metrics.pendingNotifications,
      label: 'Pending Notify',
      subtitle: 'Need notification',
      filter: { pendingNotifications: true },
    },
  ];

  if (isLoading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} className={classes.root}>
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Grid item xs={12} sm={6} md={3} key={card.id}>
            <Card 
              className={classes.card}
              onClick={() => onFilterClick && onFilterClick(card.filter)}
            >
              <CardContent className={classes.cardContent}>
                <Box className={classes.iconContainer}>
                  <Icon className={`${classes.icon} ${card.iconClass}`} />
                </Box>
                <Typography className={classes.number}>
                  {card.number}
                </Typography>
                <Typography className={classes.label}>
                  {card.label}
                </Typography>
                <Typography className={classes.subtitle}>
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MaintenanceDashboard;
